import React, { useState } from 'react';
import { Scan, PenLine } from 'lucide-react';

const printerTypes = [
  { id: 'delivery', label: 'Delivery' },
  { id: 'fulfillment', label: 'Preparación' },
  { id: 'backup', label: 'Copia Comercio' }
];

const PrinterConfigModal = ({ 
  printer, 
  open, 
  onClose,
  onSave,
  config,
  onConfigChange,
  testError,
  handleTestNewPrinter,
  testingPrinter,
  onScan,
  scanning,
  clientStatus
}) => {
  const [addMethod, setAddMethod] = useState('manual');
  
  if (!open) return null;

  const scanDisabled = scanning || clientStatus !== 'running';
  
  const getTooltip = () => {
    if (scanning) return 'Escaneando...';
    if (clientStatus !== 'running') return 'Cliente de impresión no conectado';
    return 'Buscar impresoras en la red';
  };

  const togglePrinterType = (typeId) => {
    const newTypes = config.types.includes(typeId)
      ? config.types.filter(t => t !== typeId)
      : [...config.types, typeId];
    
    onConfigChange({ ...config, types: newTypes });
  };

  const handleIPChange = (e) => {
    onConfigChange({ ...config, ip_address: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
            {printer ? 'Editar Impresora' : 'Nueva Impresora'}
          </h2>

          {!printer && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Método de Agregado
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setAddMethod('scan');
                    onScan();
                  }}
                  disabled={scanDisabled}
                  title={getTooltip()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg flex-1 ${
                    scanDisabled
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : addMethod === 'scan'
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-2 border-blue-500 dark:border-blue-400'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <Scan className="w-4 h-4" />
                  <span>Buscar en Red</span>
                </button>
                <button
                  onClick={() => setAddMethod('manual')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg flex-1 ${
                    addMethod === 'manual'
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 border-2 border-blue-500 dark:border-blue-400'
                      : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <PenLine className="w-4 h-4" />
                  <span>Manual</span>
                </button>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
              <input
                type="text"
                className="w-full border dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                value={config.name}
                onChange={e => onConfigChange({...config, name: e.target.value})}
                placeholder="ej: Impresora Principal"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">IP</label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <input
                    type="text"
                    className={`w-full border dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                      testError ? 'border-red-500 dark:border-red-500' : ''
                    }`}
                    value={config.ip_address}
                    onChange={handleIPChange}
                    placeholder="ej: 192.168.1.100"
                  />
                  {testError && (
                    <p className="mt-1 text-sm text-red-500 dark:text-red-400">
                      {testError}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleTestNewPrinter}
                  disabled={testingPrinter || !config.ip_address}
                  className={`px-4 py-2 rounded ${
                    testingPrinter
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {testingPrinter ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                      <span>Probando...</span>
                    </div>
                  ) : (
                    'Probar'
                  )}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Marca</label>
              <select
                className="w-full border dark:border-gray-700 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                value={config.brand}
                onChange={e => onConfigChange({...config, brand: e.target.value})}
              >
                <option value="epson">Epson</option>
                <option value="star">Star</option>
                <option value="kretz">Kretz</option>
                <option value="3nstar">3nStar</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tipos de Documentos</label>
              <div className="flex flex-wrap gap-2">
                {printerTypes.map(type => (
                  <button
                    key={type.id}
                    onClick={() => togglePrinterType(type.id)}
                    className={`px-3 py-1 rounded-full text-sm ${
                      config.types.includes(type.id)
                        ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 border dark:border-gray-600 rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Cancelar
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded hover:bg-blue-600 dark:hover:bg-blue-700"
            >
              {printer ? 'Guardar Cambios' : 'Agregar Impresora'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrinterConfigModal; 